import React, { useState, useEffect } from 'react';
import { DownloadOutlined, ReloadOutlined, SearchOutlined, PrinterOutlined, BookOutlined } from '@ant-design/icons';
import { Table, Row, Col, Breadcrumb, Button, Select, Form, DatePicker, Tooltip, Tag, List, Tabs, Space } from 'antd';
import { intialPaginationState, showingPageTotalRange, tailFormItemLayout, formItemLayout } from '../../common_functions';
import { STATUS } from '../../config';
import moment from 'moment';
import useReportHistory from './hooks/useReportHistory';
import BibliographyReportHistory from './BibliographyReportHistory';

const { RangePicker } = DatePicker;

const CATALOG_DEFAULT_FILTERS = { report_type: { _in: [1, 2, 3, 4] } };

const STATUS_MAP = {
  draft: { color: 'orange', label: 'Pending' },
  in_progress: { color: 'blue', label: 'In Progress' },
  published: { color: 'green', label: 'Completed' },
};

const safeParseArray = val => {
  if (Array.isArray(val)) return val;
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const renderList = items => (
  <List
    dataSource={items}
    renderItem={item => (
      <List.Item style={{ display: 'list-item', listStyleType: 'disc', marginLeft: '20px', borderBottom: 'none' }}>
        {item}
      </List.Item>
    )}
  />
);

const CatalogHistory = props => {
  const { getItems } = props;
  const { formRef, pagination, loading, history, setHistory, getHistory, handleTableChange, handleDownLoadFile, resetFilters } =
    useReportHistory(props, CATALOG_DEFAULT_FILTERS);

  const [collections, setCollections] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);

  useEffect(() => {
    (async () => {
    await getCollections();
    await getFieldsValues();
    await getHistory(intialPaginationState);
  })();
  }, []);

  const columns = [
    {
      title: 'Generated Date',
      dataIndex: 'date_created',
      key: 'date_created',
      render: date => moment(date).format('MM/DD/YYYY, h:mm:ss A'),
    },
    {
      title: 'Collection',
      dataIndex: 'collections',
      key: 'collections',
      render: collectionsNames => {
        const names = safeParseArray(collectionsNames);
        if (!names.length) return <span style={{ color: '#bbb' }}>—</span>;
        return renderList(names);
      },
      width: 240,
    },
    {
      title: 'Title',
      dataIndex: 'object_titles',
      key: 'object_titles',
      render: object_titles => {
        const titles = safeParseArray(object_titles);
        if (!titles.length) return <span style={{ color: '#bbb' }}>—</span>;
        return renderList(titles);
      },
      width: 300,
    },
    {
      title: 'No of Records in Pdf',
      dataIndex: 'number_of_records_in_pdf',
      key: 'number_of_records_in_pdf',
      align: 'center',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: status => {
        const { color, label } = STATUS_MAP[status] || { color: 'red', label: 'Failed' };
        return (
          <Tag color={color} key={status}>
            {label}
          </Tag>
        );
      },
    },
    {
      title: 'Type',
      dataIndex: 'report_type',
      key: 'report_type',
      render: text => {
        const match = reportTypes.find(t => t.value == text || t.value === Number(text));
        return <span>{match?.text || text}</span>;
      },
    },
    {
      title: 'Actions',
      key: 'action',
      fixed: 'right',
      render: (_, record) =>
        record?.report?.id ? (
          <span>
            <div className="actionbtngroup">
              {props.roleType === 'admin' || props?.tabs_permissions?.settings_report_history?.view ? (
                <Tooltip placement="top" title="Download">
                  <button className="edit-btn" onClick={() => handleDownLoadFile(record.report.id, record.report.filename_download)}>
                    <DownloadOutlined />
                  </button>
                </Tooltip>
              ) : null}
              {props.deleteRecord(
                props,
                record.id,
                'report_history',
                'this record permanently?',
                history,
                setHistory,
                props?.tabs_permissions?.settings_report_history?.delete
              )}
            </div>
          </span>
        ) : null,
    },
  ];

  const getFieldsValues = async () => {
    let { data: { meta: { options: { choices: choices_fields = [] } = {} } = {} } = {} } = await props.fetchFieldValues('report_history', {
      fields: ['report_type'],
    });
    setReportTypes(choices_fields.filter(t => [1, 2, 3, 4].includes(Number(t.value))));
  };

  const getCollections = async () => {
    const { data: collection_data } = await getItems('collections', {
      fields: ['id', 'name'],
      sort: '-id',
      limit: -1,
      filters: {
        associated_to: {
          _in: ['print', 'both'],
        },
      },
    });
    setCollections(collection_data);
  };

  const onFinish = async values => {
    let filtersitems = {};
    let condition = {};
    if (values?.date_created) {
      condition = {
        _and: [
          { date_created: { _gte: values?.date_created?.[0].format('YYYY-MM-DD') + 'T00:00:00' } },
          { date_created: { _lte: values?.date_created?.[1].format('YYYY-MM-DD') + 'T23:59:59' } },
        ],
      };
    }
    if (values?.collections?.length > 0) {
      filtersitems['collection_obj'] = { collection_obj_id: { collections: { _in: values.collections } } };
    }
    if (values?.status) {
      filtersitems['status'] = { _eq: values.status };
    }
    if (values?.report_type) {
      filtersitems['report_type'] = { _eq: values.report_type };
    }
    filtersitems = { ...filtersitems, ...condition };
    await getHistory(intialPaginationState, filtersitems);
  };

  return (
    <div className={!!loading ? 'disabledComponent' : ''} style={{ paddingTop: 20 }}>
      <Form ref={formRef} layout="vertical" onFinish={onFinish} {...formItemLayout} initialValues={{ layout: tailFormItemLayout }}>
        <Row gutter={24}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item name="collections" label="Collection">
              <Select
                placeholder="Select collection"
                optionFilterProp="children"
                maxTagCount="responsive"
                mode="multiple"
                showSearch
                allowClear
                showArrow
                filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
              >
                {collections.map(col => (
                  <Select.Option key={col.id} value={col.id}>
                    {col.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item name="date_created" label="Generated Date">
              <RangePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item name="status" label="Status">
              <Select
                placeholder="Select Location"
                optionFilterProp="children"
                showSearch
                allowClear
                filterOption={(input, option) => option.children.includes(input)}
              >
                {STATUS.map(col => (
                  <Select.Option key={col.key} value={col.key}>
                    {col.value}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <Form.Item name="report_type" label="Report Type">
              <Select
                placeholder="Select Location"
                optionFilterProp="children"
                showSearch
                allowClear
                filterOption={(input, option) => option.children.includes(input)}
              >
                {reportTypes.map(col => (
                  <Select.Option key={col.value} value={col.value}>
                    {col.text}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={9} lg={9}>
            <Form.Item>
              <Button
                type="primary"
                size="small"
                htmlType="submit"
                style={{ marginTop: '28px', height: '42px' }}
                icon={<SearchOutlined />}
              >
                Search
              </Button>
              <Button
                style={{ marginLeft: '10px', height: '42px', marginTop: '28px' }}
                size="small"
                danger
                onClick={resetFilters}
                icon={<ReloadOutlined />}
              >
                Reset Filters
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <hr />
      <div className="artist-list">
        <Table
          rowKey="id"
          scroll={{ x: 'max-content' }}
          columns={columns}
          bordered
          dataSource={history}
          pagination={history.length > 0 ? { ...pagination, ...showingPageTotalRange } : false}
          loading={loading}
          onChange={handleTableChange}
        />
      </div>
    </div>
  );
};

const ReportHistory = props => {
  const tabItems = [
    {
      key: 'catalog',
      label: (
        <Space size={8}>
          <PrinterOutlined />
          <span>Print Objects Report</span>
        </Space>
      ),
      children: <CatalogHistory {...props} />,
    },
    {
      key: 'bibliography',
      label: (
        <Space size={8}>
          <BookOutlined />
          <span>Bibliography Reports</span>
        </Space>
      ),
      children: <BibliographyReportHistory {...props} />,
    },
  ];

  return (
    <Row>
      <Col span={24} className="app-breadcrumb-col">
        <Breadcrumb items={[{ title: "Reports", href: "/reports/report-history" }, { title: "Report History" }]} />
      </Col>
      <Col className="white-paper" span={24}>
        <Tabs
          defaultActiveKey="catalog"
          type="card"
          size="large"
          tabBarGutter={8}
          tabBarStyle={{ marginBottom: 0 }}
          items={tabItems}
        />
      </Col>
    </Row>
  );
};

export default ReportHistory;
